import { NotificationResponse } from '../../../models';

const ACTION_REQUIRED_KINDS = new Set([
  'career_challenge_comprehension_required',
  'career_challenge_submission_failed',
  'career_challenge_participant_idle_nudge',
  'career_challenge_github_required',
  'career_challenge_started',
  'onboarding_idle_nudge'
]);

export function notificationMetadataKind(item: NotificationResponse): string {
  return (item.metadata?.['notificationKind'] ?? '').trim().toLowerCase();
}

export function notificationResumeIntent(item: NotificationResponse): string {
  return (item.metadata?.['resumeIntent'] ?? '').trim().toLowerCase();
}

export function notificationChallengeId(item: NotificationResponse): string {
  return (item.metadata?.['challengeId'] ?? '').trim();
}

export function notificationMetadataWeekNumber(item: NotificationResponse): number {
  const raw = item.metadata?.['weekNumber'] ?? item.metadata?.['week_number'] ?? '';
  const parsed = parseInt(String(raw), 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function notificationAchievementId(item: NotificationResponse): string {
  return (item.metadata?.['achievementId'] ?? item.metadata?.['achievement_id'] ?? '').trim();
}

export function notificationAchievementCode(item: NotificationResponse): string {
  return (item.metadata?.['achievementCode'] ?? item.metadata?.['achievement_code'] ?? '').trim();
}

export type NotificationIconType =
  | 'code'
  | 'check'
  | 'document'
  | 'trophy'
  | 'chart'
  | 'star'
  | 'doc-green'
  | 'alert'
  | 'handshake'
  | 'target'
  | 'bell';

export function getNotificationIconType(item: NotificationResponse): NotificationIconType {
  const kind = notificationMetadataKind(item);
  const title = (item.title ?? '').toLowerCase();

  if (title.includes('interview') || title.includes('reassessment ready')) {
    return 'target';
  }
  if (title.includes('registered') || kind.includes('registered')) {
    return 'handshake';
  }
  if (title.includes('could not') || title.includes('failed') || kind.includes('failed')) {
    return 'alert';
  }
  if (title.includes('being prepared') || title.includes('generating')) {
    return 'doc-green';
  }
  if (title.includes('reassessment completed') || title.includes('trophy')) {
    return 'trophy';
  }
  if (title.includes('plan complete') || title.includes('progress')) {
    return 'chart';
  }
  if (title.includes('weekly plan') || title.includes('available')) {
    return 'star';
  }
  if (title.includes('submission received') || title.includes('scoring')) {
    return 'document';
  }
  if (title.includes('is ready') || kind.includes('completed')) {
    return 'check';
  }
  if (title.includes('challenge') || title.includes('code') || title.includes('dev') || kind.includes('challenge')) {
    return 'code';
  }

  return 'bell';
}

export function isNotificationActionRequired(item: NotificationResponse): boolean {
  const metadata = item.metadata ?? {};
  if (metadata['actionRequired'] === 'true' || metadata['action_required'] === 'true') {
    return true;
  }
  return ACTION_REQUIRED_KINDS.has(notificationMetadataKind(item));
}

export function notificationDeepLinkRoute(item: NotificationResponse): string[] | null {
  const kind = notificationMetadataKind(item);
  const type = (item.type ?? '').trim().toLowerCase();
  const challengeId = notificationChallengeId(item);
  const resumeIntent = notificationResumeIntent(item);
  const cohort = (item.metadata?.['cohort'] ?? '').trim().toLowerCase();
  const weekNumber = notificationMetadataWeekNumber(item);
  const achievementId = notificationAchievementId(item);
  const achievementCode = notificationAchievementCode(item);

  // ── Achievement ─────────────────────────────────────────────────────────────
  if (kind === 'achievement' || type === 'achievement') {
    // Special case: SGA or Profile Complete achievements route to Learning or Journey
    if (
      achievementCode === 'ACHIEVEMENT_SGA_COMPLETED' ||
      achievementCode === 'ACHIEVEMENT_PROFILE_COMPLETE'
    ) {
      return ['/learning'];
    }
    if (
      achievementCode === 'ACHIEVEMENT_JOB_READINESS_70' ||
      achievementCode === 'ACHIEVEMENT_JOB_READINESS_80'
    ) {
      return ['/job-readiness'];
    }
    if (achievementId) {
      return ['/achievements', achievementId];
    }
    return ['/achievements'];
  }

  // ── Community ───────────────────────────────────────────────────────────────
  if (
    kind === 'community_post' ||
    kind === 'community_celebration' ||
    kind === 'community_discover' ||
    kind === 'community_cohort_achievement' ||
    type === 'community'
  ) {
    return ['/community'];
  }

  // ── Onboarding / Trivia / Target Role ─────────────────────────────────────────
  if (kind === 'onboarding_idle_nudge') {
    return ['/onboarding/start-direct'];
  }

  if (kind === 'target_role_set' || kind === 'profile_complete') {
    return ['/learning'];
  }

  // ── Career Challenges ───────────────────────────────────────────────────────
  if (kind === 'career_challenge_comprehension_required' || resumeIntent === 'career_challenge_comprehension') {
    if (challengeId) {
      return ['/career-challenges', challengeId, 'comprehension'];
    }
    return ['/career-challenges'];
  }

  if (kind === 'career_challenge_comprehension_analyzing') {
    if (challengeId) {
      return ['/career-challenges', challengeId, 'comprehension', 'processing'];
    }
    return ['/career-challenges'];
  }

  if (kind === 'career_challenge_participant_idle_nudge') {
    if (!challengeId) {
      return ['/career-challenges'];
    }
    if (resumeIntent === 'career_challenge_conversion' || cohort === 'conversion') {
      return ['/career-challenges', challengeId];
    }
    if (resumeIntent === 'career_challenge_submit' || cohort === 'activation') {
      return ['/career-challenges', challengeId, 'submit'];
    }
    if (resumeIntent === 'career_challenge_comprehension') {
      return ['/career-challenges', challengeId, 'comprehension'];
    }
    return ['/career-challenges', challengeId];
  }

  if (
    kind === 'career_challenge_schedule_changed' ||
    kind === 'career_challenge_published' ||
    kind === 'career_challenge_registered' ||
    kind === 'career_challenge_submission_received' ||
    kind === 'career_challenge_scored' ||
    kind === 'career_challenge_completed' ||
    kind === 'career_challenge_submission_failed' ||
    kind === 'career_challenge_under_judge_review' ||
    kind === 'career_challenge_github_required' ||
    kind === 'career_challenge_started' ||
    kind === 'career_challenge_idle_nudge_push' ||
    type === 'career_challenge'
  ) {
    if (challengeId) {
      return ['/career-challenges', challengeId];
    }
    return ['/career-challenges'];
  }

  if (challengeId) {
    return ['/career-challenges', challengeId];
  }

  // Weekly Reassessment Completed -> open week detail if weekNumber > 0
  if (kind === 'weekly_reassessment_completed' || type === 'reassessment_completed') {
    if (weekNumber > 0) {
      return ['/learning/week', String(weekNumber)];
    }
    return ['/learning'];
  }

  // Next week generating / next week generated
  if (kind === 'next_week_generating') {
    if (weekNumber > 0) {
      return ['/learning/week', String(weekNumber), 'processing'];
    }
    return ['/learning'];
  }

  if (kind === 'next_week_generated') {
    if (weekNumber > 0) {
      return ['/learning/week', String(weekNumber)];
    }
    return ['/learning'];
  }

  // Weekly conversation probe or interview reassessment ready
  if (kind === 'weekly_conversation_probe_ready') {
    if (weekNumber > 0) {
      return ['/learning/week', String(weekNumber), 'reassessment', 'chat'];
    }
    return ['/learning'];
  }

  if (kind === 'weekly_interview_reassessment_ready') {
    if (weekNumber > 0) {
      return ['/learning/week', String(weekNumber), 'reassessment'];
    }
    return ['/learning'];
  }

  // Tasks assessed / reassessing / general weekly kind
  if (
    kind === 'weekly_tasks_assessed' ||
    kind === 'weekly_reassessment_assessing' ||
    kind === 'current_week_regenerated' ||
    kind === 'week_start_summary'
  ) {
    if (weekNumber > 0) {
      return ['/learning/week', String(weekNumber)];
    }
    return ['/learning'];
  }

  // Recommendation or learning reminder related
  if (
    type === 'recommendation' ||
    type === 'learning_reminder' ||
    type === 'tasks_assessed'
  ) {
    if (weekNumber > 0) {
      return ['/learning/week', String(weekNumber)];
    }
    return ['/learning'];
  }

  // Trivia
  if (type === 'trivia' || kind === 'trivia') {
    return ['/learning'];
  }

  // Fallback if weekNumber is present on any learning notification
  if (weekNumber > 0) {
    return ['/learning/week', String(weekNumber)];
  }

  return null;
}
